import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthEmailAPI } from "@/api/authEmailApi";
import {
  AuthHeader,
  AuthShell,
  ForgotPasswordStep,
  ResetSentStep,
  getResetSentMessage,
  isValidEmail
} from "@/features/login";

/** Standalone password-reset request; the same card as /login's forgot-password step. */
export const ResetPassword = () => {
  const id = useId();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const emailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!sent) emailRef.current?.focus();
  }, [sent]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Invalid email address");
      return;
    }
    setError(null);
    setSending(true);
    try {
      await AuthEmailAPI.requestPasswordReset(email.trim());
      setSent(true);
    } catch {
      toast.error("Couldn't send the reset email. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <AuthShell>
        <AuthHeader
          title="Check Your Email"
          subtitle={getResetSentMessage(email)}
        />
        <div className="mt-11 flex flex-col">
          <ResetSentStep
            backTo="/login"
            onUseDifferentEmail={() => setSent(false)}
          />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthHeader
        title="Reset Your Password"
        subtitle="We'll email you a link to reset your password."
      />
      <form
        autoComplete="off"
        noValidate
        onSubmit={submit}
        className="mt-11 flex flex-col"
      >
        <ForgotPasswordStep
          id={`${id}-email`}
          inputRef={emailRef}
          email={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) setError(null);
          }}
          error={error}
          sending={sending}
          backTo="/login"
        />
      </form>
    </AuthShell>
  );
};
